import React, { useMemo, useCallback } from "react";
import { Alert, Box, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import CalendarHeatmap from "react-calendar-heatmap";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { readWeekStartsOnMondayPreference } from "../utils/clientPreferences";
import "react-calendar-heatmap/dist/styles.css";

type StreamDuration = {
  date: string;
  seconds: number;
};

type StreamDurationsResponse = {
  streamId?: string;
  durations?: StreamDuration[];
  error?: string;
};

type StreamCalendarProps = {
  streamId: string;
  weekStartsOnMonday?: boolean;
};

const StreamCalendar = ({ streamId, weekStartsOnMonday }: StreamCalendarProps) => {
  const theme = useTheme();
  const { authorizedFetch } = useAuth();
  const gutterSize = 1;
  const storedWeekStartsOnMonday = useMemo(() => readWeekStartsOnMondayPreference(true), []);
  const effectiveWeekStartsOnMonday = weekStartsOnMonday ?? storedWeekStartsOnMonday;

  if (!streamId) {
    return null;
  }

  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - 119);

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery<StreamDuration[]>({
    queryKey: ["stream-calendar", streamId],
    enabled: Boolean(streamId),
    refetchInterval: 30000,
    queryFn: async () => {
      const params = new URLSearchParams({
        start: startDate.toISOString().slice(0, 10),
        end: endDate.toISOString().slice(0, 10)
      });
      const response = await authorizedFetch(
        `/api/streams/${encodeURIComponent(streamId)}/online_durations?${params.toString()}`
      );
      const payload = (await response.json().catch(() => null)) as StreamDurationsResponse | null;
      if (!response.ok) {
        const message = payload?.error ?? "Unable to load stream calendar";
        throw new Error(message);
      }
      const durations = Array.isArray(payload?.durations) ? payload.durations : [];
      return durations.map((duration: StreamDuration) => ({
        date: duration.date,
        seconds: duration.seconds
      }));
    }
  });

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "2-digit",
        year: "numeric"
      }),
    []
  );

  const formatDuration = useCallback((totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const parts = [hours, minutes, seconds].map((v) => String(v).padStart(2, "0"));
    return `${parts[0]}:${parts[1]}:${parts[2]}`;
  }, []);

  const values = useMemo(() => {
    const totals = new Map<string, number>();
    (data ?? []).forEach((entry) => {
      const date = new Date(entry.date);
      if (Number.isNaN(date.getTime())) {
        return;
      }
      if (date < startDate || date > endDate) {
        return;
      }
      const dateKey = entry.date;
      const seconds = Number(entry.seconds) || 0;
      totals.set(dateKey, (totals.get(dateKey) ?? 0) + seconds);
    });
    const calendarValues = [];
    for (let cursor = new Date(startDate); cursor <= endDate; cursor.setDate(cursor.getDate() + 1)) {
      const dateKey = cursor.toISOString().slice(0, 10);
      calendarValues.push({ date: dateKey, seconds: totals.get(dateKey) ?? 0 });
    }
    return calendarValues;
  }, [data, endDate, startDate]);

  const classForValue = useCallback(
    (value: { date?: string; seconds?: number } | undefined) => {
      if (!value || !value.seconds) {
        return "color-empty";
      }
      if (value.seconds >= 8 * 3600) {
        return "color-github-4";
      }
      if (value.seconds >= 4 * 3600) {
        return "color-github-3";
      }
      if (value.seconds >= 1 * 3600) {
        return "color-github-2";
      }
      return "color-github-1";
    },
    []
  );

  const formatLabel = useCallback(
    (value: { date?: string; seconds?: number } | undefined) => {
      if (!value?.date) {
        return "No online time recorded";
      }
      const date = new Date(value.date);
      const formattedDate = Number.isNaN(date.getTime()) ? value.date : dateFormatter.format(date);
      const seconds = value.seconds ?? 0;
      if (!seconds) {
        return `${formattedDate} • No online time recorded`;
      }
      return `${formattedDate} • Stream online for ${formatDuration(seconds)}`;
    },
    [dateFormatter, formatDuration]
  );

  const tooltipDataAttrs = useCallback(
    (value: { date?: string; seconds?: number } | undefined) => {
      const label = formatLabel(value);
      return { "aria-label": label, title: label };
    },
    [formatLabel]
  );

  const weekdayLabels = useMemo(() => {
    if (!effectiveWeekStartsOnMonday) {
      return ["", "Mon", "", "Wed", "", "Fri", ""];
    }
    return ["Mon", "", "Wed", "", "Fri", "", ""];
  }, [effectiveWeekStartsOnMonday]);

  const transformDayElement = useCallback(
    (element: React.SVGProps<SVGRectElement>, _value: unknown, index: number) => {
      // The library actually passes a ReactElement at runtime, despite the type signature
      const reactElement = element as any as React.ReactElement;
      if (!effectiveWeekStartsOnMonday || !React.isValidElement(reactElement)) {
        return reactElement;
      }
      const dayIndex = index % 7;
      const newDayIndex = (dayIndex + 6) % 7; // rotate so Monday is first column
      const squareSize = Number((reactElement.props as { width?: number }).width) || 10;
      const spacing = squareSize + gutterSize;
      const x = newDayIndex * spacing;
      return React.cloneElement(reactElement, { x } as any);
    },
    [effectiveWeekStartsOnMonday, gutterSize]
  );

  if (isLoading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    const message = error instanceof Error ? error.message : "Unable to load stream calendar";
    return <Alert severity="error">{message}</Alert>;
  }

  const hasEvents = values.some((value) => (value.seconds ?? 0) > 0);

  return (
    <Box
      sx={{
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 1,
        "& .react-calendar-heatmap": {
          marginLeft: "10px"
        },
        "& .react-calendar-heatmap text": {
          fill: theme.palette.text.secondary,
          fontSize: "0.5rem"
        },
        "& .react-calendar-heatmap .react-calendar-heatmap-month-label": {
          fontSize: "0.5rem"
        },
        "& .react-calendar-heatmap .react-calendar-heatmap-day": {
          width: 8,
          height: 8
        },
        "& .react-calendar-heatmap .color-empty": {
          fill: theme.palette.action.hover
        },
        "& .react-calendar-heatmap .color-github-1": {
          fill: theme.palette.success.light
        },
        "& .react-calendar-heatmap .color-github-2": {
          fill: theme.palette.success.main
        },
        "& .react-calendar-heatmap .color-github-3": {
          fill: theme.palette.success.dark
        },
        "& .react-calendar-heatmap .color-github-4": {
          fill: theme.palette.success.dark,
          opacity: 0.8
        }
      }}
    >
      <CalendarHeatmap
        startDate={startDate}
        endDate={endDate}
        values={values}
        classForValue={classForValue}
        gutterSize={gutterSize}
        horizontal={false}
        showMonthLabels
        showWeekdayLabels
        tooltipDataAttrs={tooltipDataAttrs}
        titleForValue={formatLabel}
        weekdayLabels={weekdayLabels as [string, string, string, string, string, string, string]}
        transformDayElement={transformDayElement}
      />
      <Stack spacing={0.5} alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Online time over the last 120 days
        </Typography>
        {!hasEvents ? (
          <Typography variant="body2" color="text.secondary">
            No online time recorded for this stream yet.
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

export default StreamCalendar;
